import VueDraggableResizable from 'vue-draggable-resizable';
import 'vue-draggable-resizable/dist/VueDraggableResizable.css';
import EditorBase from './EditorBase';
import { Component, Prop } from 'vue-property-decorator';
import {
  EComponentType,
  EEditorType,
  IAlertBoxComponent,
  IComponent,
  ICustomComponent,
  ISLWidgetComponent,
  IVideoComponent,
} from '@/helpers/editor';
import AlertBoxTemplate from '@/components/editor/templates/AlertBoxTemplate.vue';
import ImageTemplate from '@/components/editor/templates/ImageTemplate.vue';
import TextTemplate from '@/components/editor/templates/TextTemplate.vue';
import VideoTemplate from '@/components/editor/templates/VideoTemplate.vue';
import CustomComponentTemplate from '@/components/editor/templates/CustomComponentTemplate.vue';
import WebcamTemplate from '@/components/editor/templates/WebcamTemplate.vue';
import EventListTemplate from '@/components/editor/templates/EventListTemplate.vue';
import ChatTemplate from '@/components/editor/templates/ChatTemplate.vue';
import StreamLabelTemplate from './templates/StreamLabelTemplate.vue';
import GameCaptureTemplate from './templates/GameCaptureTemplate.vue';

const UNLOAD_SELECTED_COMPONENT_CONTAINER_IDS = ['canvas', 'canvas-area', 'vertical-canvas', 'vertical-canvas-area'];

@Component({
  components: {
    VueDraggableResizable,
  },
})
export default class EditorComponent extends EditorBase {
  @Prop({ required: true })
  component: IComponent | IVideoComponent | IAlertBoxComponent | ICustomComponent | ISLWidgetComponent;

  @Prop({ required: true })
  positionZ: number;

  @Prop({ default: false })
  isVertical: boolean;

  @Prop({ default: 1 })
  scaleFactor: number;

  timeoutId = null;
  selectedComponentIsHover: boolean = false;

  get componentRef(): string {
    let name: string = this.getComponentRefName(this.component);

    if (this.isVertical) name = `${name}-vertical`;

    return name;
  }

  get componentLayoutData(): Partial<IComponent> {
    if (this.isVertical) {
      return this.getVerticalSettingsPosition(this.component);
    }

    return {
      x: Math.round(this.component.x),
      y: Math.round(this.component.y),
      w: Math.round(this.component.w),
      h: Math.round(this.component.h),
      lockAspectRatio: this.component.lockAspectRatio,
      aspectRatio: this.component.aspectRatio,
    };
  }

  get isActive(): boolean {
    if (!this.selectedEditor) {
      return this.selectedComponent?.id === this.component?.id;
    }

    const currentEditor = this.isVertical ? EEditorType.vertical : EEditorType.horizontal;

    return this.selectedComponent?.id === this.component?.id && this.selectedEditor === currentEditor;
  }

  get resizeHandles(): string[] {
    return ['tl', 'tr', 'br', 'bl'];
  }

  get isLocked(): boolean {
    return this.component.locked;
  }

  get isResizable(): boolean {
    return !this.isLocked && !this.component.isFullWidth;
  }

  get isHidden(): boolean {
    if (this.isVertical) return this.component.verticalSettings?.hidden;

    return this.component.hidden;
  }

  get axisHandles(): string {
    return this.component.lockHorizontal ? 'y' : 'both';
  }

  componentTemplate() {
    switch (this.component.type) {
      case EComponentType.text:
        return TextTemplate;
      case EComponentType.image:
        return ImageTemplate;
      case EComponentType.video:
        return VideoTemplate;
      case EComponentType.alertBox:
        return AlertBoxTemplate;
      case EComponentType.customComponent:
        return CustomComponentTemplate;
      case EComponentType.webcam:
        return WebcamTemplate;
      case EComponentType.chat:
        return ChatTemplate;
      case EComponentType.eventList:
        return EventListTemplate;
      case EComponentType.streamLabel:
        return StreamLabelTemplate;
      case EComponentType.gameCapture:
        return GameCaptureTemplate;
      default:
        return null;
    }
  }

  templateData() {
    return {
      component: this.component,
      isEditor: true,
      isFocused: this.selectedComponent?.id === this.component.id,
      zIndex: this.positionZ,
      themeId: this.selectedScene?.theme_id,
      isVertical: this.isVertical,
    };
  }

  onActivated() {
    this.selectedComponent = this.component;
    this.selectedEditor = this.isVertical ? EEditorType.vertical : EEditorType.horizontal;
    this.selectedComponentIsHover = true;
  }

  onResizeStop(x, y, w, h) {
    if (w <= 0 || h <= 0) return;

    const position: Partial<IComponent> = {
      x: Math.round(x),
      y: Math.round(y),
      w: Math.round(w),
      h: Math.round(h),
    };

    let data: Partial<IComponent> = {
      id: this.component.id,
    };

    if (this.isVertical) {
      data.verticalSettings = position;
    } else {
      data = { ...data, ...position };
    }

    this.onComponentUpdate(data);
  }

  onDragging(x, y) {
    const position: Partial<IComponent> = {
      x: Math.round(x),
      y: Math.round(y),
      w: this.componentLayoutData.w,
      h: this.componentLayoutData.h,
    };

    let data: Partial<IComponent> = {
      id: this.component.id,
    };

    if (this.isVertical) {
      data.verticalSettings = position;
    } else {
      data = { ...data, ...position };
    }

    this.onComponentUpdate(data);
  }

  get handleScale(): number {
    return 1 / (this.scaleFactor || this.scale);
  }

  /*
   *
   * adding unsavedChange Flag and a componentUpdate Step here to avoid
   * updating the complete list of components without any real change.
   *
   */
  onDragStart(event) {
    if (event.type === 'mousedown' && this.isMobile) return false;

    this.addComponentUpdateUndo();
    this.hasUnsavedChange = true;

    return true;
  }

  onCanvasClickOutside(event) {
    this.preventComponentDeactivate = true;

    if (!this.isActive) return;

    if (UNLOAD_SELECTED_COMPONENT_CONTAINER_IDS.includes(event.srcElement.id)) {
      this.preventComponentDeactivate = false;
      this.selectedComponentIsHover = false;
      this.selectedComponent = null;
    }
  }

  onClick() {
    if (!this.selectedComponentIsHover || !this.isActive) {
      return;
    }

    this.selectedComponentIsHover = false;

    this.timeoutId = setTimeout(() => {
      this.selectedComponentIsHover = true;
      this.timeoutId = null;
    }, 100);
  }

  onMouseOver() {
    if (!this.isActive) return;

    this.selectedComponentIsHover = true;
  }

  onMouseLeave() {
    if (!this.isActive) return;

    this.selectedComponentIsHover = false;
  }
}
